import type { HomeSectionConfig, HomeSectionKey } from "../type/homePage";

/**
 * Daftar section halaman Home, diurutkan sesuai urutan tampil di website
 * (atas -> bawah). Placeholder diambil dari wording pada desain Home Page.
 *
 * Menambah section baru: cukup tambahkan satu blok di array ini (dan
 * key-nya di `HomeSectionKey`) - list & form edit akan ikut otomatis.
 *
 * `translatable: true` menandai field yang tampil sebagai kalimat/teks ke
 * pengunjung website (judul, deskripsi, dst) -> diedit sebagai pasangan
 * Indonesia + English berdampingan. Field yang TIDAK ditandai (link,
 * nomor telepon, email, nama perusahaan/orang, kode material, angka) tetap
 * satu input, sama untuk kedua bahasa -- karena bukan konten yang perlu
 * diterjemahkan.
 */
export const HOME_SECTIONS: HomeSectionConfig[] = [
  {
    key: "header",
    title: "Header",
    description: "Logo dan menu navigasi di bagian atas halaman.",
    fields: [
      {
        key: "logo",
        label: "Logo",
        type: "image",
        required: true,
      },
    ],
    items: {
      label: "Menu Items",
      itemLabel: "Menu",
      titleKey: "label",
      min: 1,
      max: 8,
      fields: [
        {
          key: "label",
          label: "Label",
          type: "text",
          required: true,
          placeholder: "Home",
          translatable: true,
        },
        {
          key: "link",
          label: "Link",
          type: "text",
          required: true,
          placeholder: "/ atau https://...",
        },
      ],
    },
  },
  {
    key: "hero",
    title: "Hero Banner",
    description: "Judul utama, tombol, dan gambar di banner paling atas.",
    fields: [
      {
        key: "title",
        label: "Title",
        type: "textarea",
        required: true,
        rows: 4,
        placeholder: "Solusi Seal Industri Presisi Tinggi & Manufaktur Kustom.",
        translatable: true,
      },
      {
        key: "button_label",
        label: "Button Label",
        type: "text",
        required: true,
        placeholder: "Hubungi Kami",
        translatable: true,
      },
      {
        key: "button_link",
        label: "Button Link",
        type: "text",
        placeholder: "/contact atau https://...",
      },
      {
        key: "image",
        label: "Hero Image",
        type: "image",
        required: true,
      },
    ],
  },
  {
    key: "categories",
    title: "Product Highlights",
    description:
      "Card produk yang tampil di bawah hero (Seal Product, Mechanical Seal, dst).",
    fields: [],
    items: {
      label: "Cards",
      itemLabel: "Card",
      titleKey: "title",
      min: 1,
      fields: [
        {
          key: "title",
          label: "Title",
          type: "text",
          required: true,
          placeholder: "Seal Product",
          translatable: true,
        },
        {
          key: "description",
          label: "Description",
          type: "textarea",
          rows: 3,
          placeholder: "Menyediakan berbagai profil seal",
          translatable: true,
        },
        {
          key: "link",
          label: "Link",
          type: "text",
          placeholder: "/product atau https://...",
        },
        {
          key: "image",
          label: "Image",
          type: "image",
          required: true,
        },
      ],
    },
  },
  {
    key: "about",
    title: "About Us",
    description: "Profil singkat perusahaan beserta foto dan tombol.",
    fields: [
      {
        key: "section_title",
        label: "Section Title",
        type: "text",
        required: true,
        placeholder: "Tentang Kami",
        translatable: true,
      },
      {
        key: "description",
        label: "Description",
        type: "textarea",
        required: true,
        rows: 10,
        placeholder: "PT. ARUNIKA MEDINA PRATAMA adalah sebuah perusahaan...",
        hint: "Tekan Enter untuk pindah paragraf.",
        translatable: true,
      },
      {
        key: "button_label",
        label: "Button Label",
        type: "text",
        placeholder: "Tentang Kami",
        translatable: true,
      },
      {
        key: "button_link",
        label: "Button Link",
        type: "text",
        placeholder: "/about atau https://...",
      },
      {
        key: "image",
        label: "Image",
        type: "image",
        required: true,
      },
    ],
  },
  {
    key: "materials",
    title: "Quality Materials",
    description: "Daftar material (NBR, EPDM, PTFE, PU, SIL, dst).",
    fields: [
      {
        key: "section_title",
        label: "Section Title",
        type: "text",
        required: true,
        placeholder: "Quality Materials",
        translatable: true,
      },
    ],
    items: {
      label: "Materials",
      itemLabel: "Material",
      titleKey: "name",
      min: 1,
      fields: [
        {
          key: "name",
          label: "Name",
          type: "text",
          required: true,
          placeholder: "NBR",
        },
        {
          key: "description",
          label: "Description",
          type: "text",
          placeholder: "Nitrile Rubber",
          translatable: true,
        },
      ],
    },
  },
  {
    key: "industries",
    title: "Serving Various Industries",
    description: "Daftar industri yang dilayani beserta ikonnya.",
    fields: [
      {
        key: "section_title",
        label: "Section Title",
        type: "text",
        required: true,
        placeholder: "Serving Various Industries",
        translatable: true,
      },
    ],
    items: {
      label: "Industries",
      itemLabel: "Industry",
      titleKey: "name",
      min: 1,
      fields: [
        {
          key: "name",
          label: "Name",
          type: "text",
          required: true,
          placeholder: "Manufaktur",
          translatable: true,
        },
        {
          key: "icon",
          label: "Icon",
          type: "image",
          required: true,
        },
      ],
    },
  },
  {
    key: "recent_project",
    title: "Recent Project",
    description: "Judul section dan tombol lihat semua proyek.",
    fields: [
      {
        key: "section_title",
        label: "Section Title",
        type: "text",
        required: true,
        placeholder: "Recent Project",
        translatable: true,
      },
      {
        key: "button_label",
        label: "Button Label",
        type: "text",
        placeholder: "View All",
        translatable: true,
      },
      {
        key: "button_link",
        label: "Button Link",
        type: "text",
        placeholder: "/project atau https://...",
      },
    ],
    note: {
      text: "Gambar dan detail proyek yang tampil di section ini dikelola melalui menu Recent Project.",
      linkLabel: "Buka Recent Project",
      path: "/project",
    },
  },
  {
    key: "clients",
    title: "Our Client",
    description: "Logo client yang tampil di Home.",
    fields: [
      {
        key: "section_title",
        label: "Section Title",
        type: "text",
        required: true,
        placeholder: "Our Client",
        translatable: true,
      },
    ],
    items: {
      label: "Clients",
      itemLabel: "Client",
      titleKey: "name",
      fields: [
        {
          key: "name",
          label: "Client Name",
          type: "text",
          required: true,
          placeholder: "Nama perusahaan",
        },
        {
          key: "logo",
          label: "Logo",
          type: "image",
          required: true,
        },
      ],
    },
  },
  {
    key: "client_story",
    title: "Client Story",
    description: "Testimoni client (foto, nama, dan kutipan).",
    fields: [
      {
        key: "section_title",
        label: "Section Title",
        type: "text",
        required: true,
        placeholder: "Client Story",
        translatable: true,
      },
    ],
    items: {
      label: "Stories",
      itemLabel: "Story",
      titleKey: "name",
      fields: [
        {
          key: "name",
          label: "Name",
          type: "text",
          required: true,
          placeholder: "Ahmad Khoiri",
        },
        {
          key: "quote",
          label: "Quote",
          type: "textarea",
          required: true,
          rows: 4,
          placeholder: "Testimoni client",
          translatable: true,
        },
        {
          key: "photo",
          label: "Photo",
          type: "image",
        },
      ],
    },
  },
  {
    key: "achievements",
    title: "Pencapaian Industri",
    description: "Angka pencapaian (jumlah produk, klien puas, tenaga ahli).",
    fields: [
      {
        key: "section_title",
        label: "Section Title",
        type: "text",
        required: true,
        placeholder: "Pencapaian Industri",
        translatable: true,
      },
    ],
    items: {
      label: "Achievements",
      itemLabel: "Achievement",
      titleKey: "label",
      min: 1,
      fields: [
        {
          key: "value",
          label: "Value",
          type: "text",
          required: true,
          placeholder: "3.000+",
        },
        {
          key: "label",
          label: "Label",
          type: "text",
          required: true,
          placeholder: "Jumlah Produk Dibuat",
          translatable: true,
        },
      ],
    },
  },
  // ---------------------------------------------------------------------
  // Halaman About Us
  // ---------------------------------------------------------------------
  {
    key: "about_hero",
    page: "About Us",
    title: "About Us - Hero",
    description: "Judul, deskripsi, dan foto di bagian atas halaman About Us.",
    fields: [
      {
        key: "title",
        label: "Title",
        type: "text",
        required: true,
        placeholder: "Tentang Kami",
        translatable: true,
      },
      {
        key: "description",
        label: "Description",
        type: "textarea",
        required: true,
        rows: 10,
        placeholder: "PT. ARUNIKA MEDINA PRATAMA adalah sebuah perusahaan...",
        hint: "Tekan Enter untuk pindah paragraf.",
        translatable: true,
      },
      {
        key: "image",
        label: "Image",
        type: "image",
        required: true,
      },
    ],
  },
  {
    key: "vision",
    page: "About Us",
    title: "About Us - Visi",
    description: "Judul, foto, dan daftar poin Visi perusahaan.",
    fields: [
      {
        key: "section_title",
        label: "Section Title",
        type: "text",
        required: true,
        placeholder: "VISI",
        translatable: true,
      },
      {
        key: "image",
        label: "Image",
        type: "image",
        required: true,
      },
    ],
    items: {
      label: "Points",
      itemLabel: "Point",
      titleKey: "text",
      min: 1,
      fields: [
        {
          key: "text",
          label: "Point",
          type: "textarea",
          required: true,
          rows: 3,
          placeholder: "Menjadi perusahaan yang profesional...",
          translatable: true,
        },
      ],
    },
  },
  {
    key: "mission",
    page: "About Us",
    title: "About Us - Misi",
    description: "Judul, foto, dan daftar poin Misi perusahaan.",
    fields: [
      {
        key: "section_title",
        label: "Section Title",
        type: "text",
        required: true,
        placeholder: "MISI",
        translatable: true,
      },
      {
        key: "image",
        label: "Image",
        type: "image",
        required: true,
      },
    ],
    items: {
      label: "Points",
      itemLabel: "Point",
      titleKey: "text",
      min: 1,
      fields: [
        {
          key: "text",
          label: "Point",
          type: "textarea",
          required: true,
          rows: 3,
          placeholder: "Membangun perusahaan dengan mengedepankan kepuasan konsumen.",
          translatable: true,
        },
      ],
    },
  },
  {
    key: "core_values",
    page: "About Us",
    title: "About Us - Nilai Utama",
    description: "Kartu Nilai Utama Kami (ikon, judul, deskripsi).",
    fields: [
      {
        key: "section_title",
        label: "Section Title",
        type: "text",
        required: true,
        placeholder: "Nilai Utama Kami",
        translatable: true,
      },
    ],
    items: {
      label: "Values",
      itemLabel: "Value",
      titleKey: "title",
      min: 1,
      max: 8,
      fields: [
        {
          key: "title",
          label: "Title",
          type: "text",
          required: true,
          placeholder: "Professional",
          translatable: true,
        },
        {
          key: "description",
          label: "Description",
          type: "textarea",
          required: true,
          rows: 3,
          placeholder: "Pengelolaan SDM Optimal untuk kepuasan klient tertinggi",
          translatable: true,
        },
        {
          key: "icon",
          label: "Icon",
          type: "image",
          required: true,
        },
      ],
    },
  },
  {
    key: "footer",
    title: "Footer & Contact",
    description: "Alamat Head Office, Workshop, dan kontak perusahaan.",
    fields: [
      {
        key: "head_office",
        label: "Head Office Address",
        type: "textarea",
        required: true,
        rows: 4,
        placeholder: "Alamat lengkap Head Office",
      },
      {
        key: "workshop",
        label: "Workshop Address",
        type: "textarea",
        required: true,
        rows: 4,
        placeholder: "Alamat lengkap Workshop",
      },
      {
        key: "phone",
        label: "Phone",
        type: "text",
        placeholder: "021xxxxxxx",
      },
      {
        key: "whatsapp",
        label: "WhatsApp",
        type: "text",
        placeholder: "62812xxxxxxx",
        hint: "Juga dipakai untuk tombol WhatsApp melayang di Home.",
      },
      {
        key: "email",
        label: "Email",
        type: "email",
        placeholder: "info@perusahaan.com",
      },
      {
        key: "instagram",
        label: "Instagram",
        type: "text",
        placeholder: "https://instagram.com/...",
      },
      {
        key: "logo",
        label: "Footer Logo",
        type: "image",
      },
    ],
  },
];

export const getSectionConfig = (
  key: string | undefined
): HomeSectionConfig | undefined =>
  HOME_SECTIONS.find((section) => section.key === (key as HomeSectionKey));